import React, { useState } from 'react';
import { css, cx } from 'emotion';
import AccessibilityControlIcon from '../../assets/accessibilityControlIcon.svg?react';
import CloseIcon from '../../assets/closeBolded.svg?react';
import ZoomOutIcon from '../../assets/zoomOut.svg?react';
import ZoomInIcon from '../../assets/zoomIn.svg?react';
import HighContrastIcon from '../../assets/highContrast.svg?react';
import LowerContrastIcon from '../../assets/lowerContrast.svg?react';
import ResetIcon from '../../assets/reset.svg?react';
import useComponentVisible from '../../utils/useComponentVisible';

const modalStyle = css`
  position: fixed; /* Stay in place */
  z-index: 6; /* Sit on top */
  left: 0;
  top: 0;
  overflow: auto;
  width: 100%; /* Full width */
  height: 100%;
  background-color: rgb(0, 0, 0); /* Fallback color */
  background-color: rgba(0, 0, 0, 0.4); /* Black w/ opacity */
`;
const accessibilityContainerDisplayStyle = css`
  display: flex;
`;
const stickyAccessibilityContainerStyle = css`
  position: fixed;
  z-index: 6;
  transition: right 0.5s linear;
  right: -14.15rem;
  top: 8.5rem;
`;
const stickyAccessibilityContainerOpenedStyle = css`
  transition: right 0.5s linear;
  right: 0;
`;
const accessibilityIconStyle = css`
  display: flex;
  flex-direction: column;
  background-color: var(--darkBlue);
  padding: 0.75rem;
  border: none;
  height: 4.5rem;
  font-size: 0.875rem;
  line-height: 1.5em;
  color: var(--white);
  > svg {
    height: 3rem;
    width: 3rem;
  }
  path {
    fill: var(--white);
  }
  @media (max-width: 768px) {
    font-size: 0.75rem;
    height: 3rem;
    padding: 0.5rem;
    > svg {
      height: 2rem;
      width: 2rem;
    }
  }
`;
const buttonCloseIconStyle = css`
  padding: 0.75rem 1.125rem;
  place-items: center;
  > svg {
    width: 1.5rem;
    height: 1.5rem;
    margin-bottom: 0.25rem;
  }
  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
    > svg {
      height: 1rem;
      width: 1rem;
    }
  }
`;

const accessibilityContainerStyle = css`
  background-color: var(--white);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  > h1 {
    font-size: 1.25rem;
    line-height: 1.5em;
    color: var(--dark);
    margin-bottom: 0.75rem;
  }
  > button {
    font-size: 1rem;
    text-align: right;
    line-height: 1.5em;
    border: none;
    background-color: unset;
    margin-bottom: 1rem;
    > svg {
      margin-right: 0.75rem;
      height: 1.25rem;
      width: 1.25rem;
      > path {
        stroke: var(--dark);
      }
    }
  }
`;

export const AccessibilityContainer = () => {
  const [currentTheme, setCurrentTheme] = useState(document.documentElement.getAttribute('data-theme'));
  const [fontSizePercent, setFontSizePercent] = useState(100);
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

  const resetDefault = () => {
    const root = document.querySelector(':root');
    root.style.fontSize = '100%';
    document.documentElement.setAttribute('data-theme', 'light');
    setCurrentTheme('light');
    setFontSizePercent(100);
  };

  const changeFontSize = operation => {
    const root = document.querySelector(':root');
    const style = window.getComputedStyle(root, null).getPropertyValue('font-size');
    const fontSize = parseFloat(style);
    if (operation === 'increase') {
      const newFontSize = Math.min((fontSize / 16) * 100 + 5, 130);
      root.style.fontSize = `${newFontSize}%`;
      setFontSizePercent(newFontSize);
    } else if (operation === 'decrease') {
      const newFontSize = Math.min((fontSize / 16) * 100 - 5, 130);
      setFontSizePercent(newFontSize);
      root.style.fontSize = `${newFontSize}%`;
    }
  };

  const changeTheme = targetTheme => {
    setCurrentTheme(targetTheme);
    document.documentElement.setAttribute('data-theme', targetTheme);
  };

  return (
    <>
      <div className={isComponentVisible ? modalStyle : ''} />
      <div
        ref={ref}
        className={cx(
          stickyAccessibilityContainerStyle,
          isComponentVisible ? stickyAccessibilityContainerOpenedStyle : ''
        )}
      >
        <div className={accessibilityContainerDisplayStyle}>
          <button
            className={cx(accessibilityIconStyle, isComponentVisible ? buttonCloseIconStyle : '')}
            type="button"
            aria-label="accessibility"
            onClick={() => setIsComponentVisible(!isComponentVisible)}
          >
            {isComponentVisible && (
              <>
                <CloseIcon />
                Close
              </>
            )}
            {!isComponentVisible && <AccessibilityControlIcon />}
          </button>
          <div className={accessibilityContainerStyle}>
            <h1>Accessibility controls</h1>
            <button type="button" disabled={fontSizePercent >= 130} onClick={() => changeFontSize('increase')}>
              <ZoomInIcon />
              Make text bigger
            </button>
            <button type="button" disabled={fontSizePercent <= 90} onClick={() => changeFontSize('decrease')}>
              <ZoomOutIcon />
              Make text smaller
            </button>
            <button type="button" disabled={currentTheme === 'dark'} onClick={() => changeTheme('dark')}>
              <HighContrastIcon />
              Higher contrast
            </button>
            <button type="button" disabled={currentTheme === 'light'} onClick={() => changeTheme('light')}>
              <LowerContrastIcon />
              Lower contrast
            </button>
            <button type="button" onClick={() => resetDefault()}>
              <ResetIcon />
              Reset
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
